import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import { VisitorProvider } from "../components/context/VisitorContext";
import { useVisitorRole } from "../utils/useVisitorRole";
import { VISITOR_REGISTER_SUBMODULE_SLUG } from "../utils/visitorPermissionSlugs";
import { useVisitorInlineEdit } from "../utils/useVisitorInlineEdit";
import VisitorEditPanel from "../components/form/VisitorEditPanel";
import { useVisitorDashboardStats } from "../utils/useVisitorDashboardStats";
import { notifyVisitorDataChanged } from "../utils/visitorDataEvents";
import { handlePrintVisitorSlipStub } from "../utils/visitorSlip";
import OfficeRegisterForm from "../components/office/OfficeRegisterForm";
import ReceptionistRegisterForm from "../components/receptionist/ReceptionistRegisterForm";
import VisitorLiveStatsPanel from "../components/metrics/VisitorLiveStatsPanel";
import VisitorDirectorySection from "../components/directory/VisitorDirectorySection";
import ExitVisitorModal from "../components/modals/ExitVisitorModal";
import DeleteVisitorModal from "../components/modals/DeleteVisitorModal";
import "../styles/visitorDashboard.css";
import "../styles/visitorTable.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

const REGISTER_QUICK_JUMP = [
  { key: "stats", label: "Summary", targetId: "visitor-stats" },
  { key: "register", label: "Capture visitor entry", targetId: "visitor-register" },
  { key: "list", label: "Visitor directory", targetId: "visitor-list" },
];

function filterQuickJump(items, { showSummary, showCapture, showDirectory, editing }) {
  return items.filter((item) => {
    if (item.key === "stats") return showSummary;
    if (item.key === "register") return showCapture || Boolean(editing);
    if (item.key === "list") return showDirectory;
    return true;
  });
}

function VisitorRegisterContent() {
  const location = useLocation();
  const legacyEditHandled = useRef(false);
  const perms = useVisitorRole(VISITOR_REGISTER_SUBMODULE_SLUG);
  const isReceptionist = perms.isReceptionist;
  const limitedEdit = perms.canLimitedEdit && !perms.canFullEdit;
  const {
    editingVisitor,
    editError,
    openEdit,
    openEditById,
    cancelEdit,
    clearEdit,
  } = useVisitorInlineEdit("visitor-register");

  const RegisterForm = isReceptionist ? ReceptionistRegisterForm : OfficeRegisterForm;
  const showSummary = perms.canView;
  const showCapture = perms.canCreate || (Boolean(editingVisitor) && perms.canEdit);
  const showDirectory = perms.canView;
  const hasAnyAccess = showSummary || showCapture || showDirectory;

  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [exitTarget, setExitTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [registerFormKey, setRegisterFormKey] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  const { dashboardStats, loadingStats, refreshStats } = useVisitorDashboardStats({
    enabled: showSummary,
    reloadToken,
    listScope: "self",
  });

  useEffect(() => {
    const editId = location.state?.editVisitorId;
    if (!editId || legacyEditHandled.current) return;
    legacyEditHandled.current = true;
    openEditById(editId);
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.state?.editVisitorId, location.pathname, openEditById]);

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    clearEdit();
    setReloadToken((t) => t + 1);
    notifyVisitorDataChanged();
    refreshStats({ silent: true });
  };

  const handleEditSuccess = (msg) => {
    handleSuccess(msg);
    cancelEdit();
  };

  const quickJumpItems = filterQuickJump(REGISTER_QUICK_JUMP, {
    showSummary,
    showCapture,
    showDirectory,
    editing: editingVisitor,
  });

  const handleRegisterSuccess = (msg) => {
    handleSuccess(msg);
    setRegisterFormKey((k) => k + 1);
    if (showDirectory) {
      requestAnimationFrame(() => {
        document.getElementById("visitor-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const emptyBaseline = perms.canCreate
    ? "No visitor entries registered by you yet. Use Capture visitor entry above to add the first visitor."
    : "No visitor entries registered by you yet.";

  const heroEyebrow = isReceptionist ? "Front desk" : "Office administration";

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">{heroEyebrow}</p>
          <h2 className="user-builder-hero__title">Visitor register</h2>
          <p className="user-builder-hero__sub">
            Capture new visitors and review your directory. Permissions follow visitor-register in User
            builder.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalSuccessFlashModal
        message={warningMsg}
        onDismiss={() => setWarningMsg("")}
        title="Warning"
        tone="warning"
      />
      <GlobalFeedback type="error" text={editError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="visitor-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !hasAnyAccess ? (
        <GlobalFeedback
          type="warning"
          text="No permission for visitor register. Enable View and/or Create on visitor-register in User builder for this role, then sign in again."
          role="status"
        />
      ) : null}
      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && showSummary ? (
            <VisitorLiveStatsPanel
              sectionId="visitor-stats"
              loading={loadingStats}
              dashboardStats={dashboardStats}
            />
          ) : null}

          {!perms.permissionsLoading && showCapture ? (
            <section
              id="visitor-register"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              {editingVisitor ? (
                perms.canEdit ? (
                  <VisitorEditPanel
                    visitor={editingVisitor}
                    limitedEdit={limitedEdit}
                    onCancel={cancelEdit}
                    onSuccess={handleEditSuccess}
                  />
                ) : null
              ) : perms.canCreate ? (
                <>
                  <div className="user-builder-panel__head">
                    <h3 className="user-builder-panel__title">Capture visitor entry</h3>
                    <p className="user-builder-panel__hint">
                      Record a new visitor for your office or reception lane.
                    </p>
                  </div>
                  <RegisterForm key={registerFormKey} onSuccess={handleRegisterSuccess} />
                </>
              ) : null}
            </section>
          ) : null}

          {!perms.permissionsLoading && showDirectory ? (
            <div id="visitor-list" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
              <VisitorDirectorySection
                listScope="self"
                showPanelHead
                permissions={perms}
                roleSlug={perms.roleSlug}
                reloadToken={reloadToken}
                emptyBaseline={emptyBaseline}
                onEdit={perms.canEdit ? openEdit : undefined}
                onDelete={perms.canDelete ? setDeleteTarget : undefined}
                onExit={perms.canExit ? setExitTarget : undefined}
                onSlip={perms.canSlip ? () => handlePrintVisitorSlipStub() : undefined}
              />
            </div>
          ) : null}
        </div>

        {quickJumpItems.length > 0 ? (
          <GlobalQuickJump
            asideAriaLabel="Visitor register sections"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>

      <ExitVisitorModal
        visitor={exitTarget}
        onClose={() => setExitTarget(null)}
        onSuccess={handleSuccess}
      />

      <DeleteVisitorModal
        visitor={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={handleSuccess}
        onError={(msg) => setWarningMsg(msg)}
      />
    </div>
  );
}

/** `/app/visitors/register` — Summary, capture, and directory (visitor-register). */
export default function VisitorRegisterPage() {
  return (
    <VisitorProvider>
      <VisitorRegisterContent />
    </VisitorProvider>
  );
}
